import { Link } from "react-router-dom";
import { Phone, Mail, Sparkles, Beaker, ShieldCheck, ClipboardCheck } from "lucide-react";
import { CONTACT } from "@/lib/constants";

const PILLARS = [
    { icon: Beaker, title: "Independent editorial", copy: "Our team writes reviews using manufacturer specs, published documentation, independent research, and community feedback — never sponsored copy dressed up as opinion." },
    { icon: ShieldCheck, title: "Editorial independence", copy: "Affiliate revenue and advertising never influence our rankings. If a printer isn't good, we say so — regardless of the brand." },
    { icon: ClipboardCheck, title: "Transparent methodology", copy: "Every review discloses the sources behind its assessment. Where we describe test results, we're clear about scale and scope — no cherry-picked benchmarks." },
];

export default function About() {
    return (
        <div data-testid="about-page">
            <section className="container-tight py-16 md:py-24 border-b border-zinc-800">
                <div className="eyebrow">About</div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mt-3 max-w-3xl leading-[1.02]">
                    Printer advice, <span className="text-zinc-500">from people who</span>{" "}
                    <span className="text-blue-500">actually print.</span>
                </h1>
                <p className="text-lg text-zinc-400 mt-6 max-w-2xl leading-relaxed">
                    All About Printers is an independent editorial publication for anyone who takes their printing seriously —
                    from small offices choosing their first laser to studios investing in a production-grade 3D printer.
                    We research, compare, and write it up honestly.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                    <Link to="/contact" className="btn-primary" data-testid="about-contact-cta">
                        <Mail className="h-4 w-4" /> Get in touch
                    </Link>
                    <a href={CONTACT.phoneHref} className="btn-ghost" data-testid="about-phone-cta">
                        <Phone className="h-4 w-4" /> Call {CONTACT.phoneDisplay}
                    </a>
                </div>
            </section>

            <section className="container-tight py-20 md:py-24">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
                    {PILLARS.map((p, i) => (
                        <div key={p.title} className="bg-zinc-950 p-8">
                            <div className="font-mono text-xs text-zinc-500">0{i + 1}</div>
                            <p.icon className="h-6 w-6 text-blue-400 mt-6" />
                            <div className="font-display text-lg font-semibold text-white mt-4 tracking-tight">{p.title}</div>
                            <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{p.copy}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="container-tight py-16 md:py-20 border-t border-zinc-800">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                        <div className="eyebrow">Our methodology</div>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">
                            How we score printers
                        </h2>
                        <div className="mt-6 space-y-4 text-zinc-300 leading-relaxed">
                            <p>
                                Printers we cover are scored across print quality, speed, cost per page, connectivity,
                                and long-term reliability using a combination of manufacturer specs, published third-party
                                data, and community feedback. Ratings are weighted for the printer&apos;s intended use — we don&apos;t
                                penalize a laser for weak photo output or an inkjet for slower text speeds.
                            </p>
                            <p>
                                We publish full specs, editorial notes, and pros/cons for every printer. Where our team
                                has direct experience with a machine, we note that context. Content is for informational and
                                educational purposes only — always verify current specs, pricing, and availability with the
                                manufacturer before purchasing.
                            </p>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="card-flat p-6 md:p-8">
                            <Sparkles className="h-6 w-6 text-yellow-400" />
                            <div className="font-display text-xl font-semibold text-white mt-4 tracking-tight">Editor&apos;s note</div>
                            <p className="text-zinc-400 mt-3 text-sm leading-relaxed">
                                We&apos;re grateful you&apos;re here. If a review helped you make a decision — or if we got something wrong —
                                let us know. Reader feedback shapes our future reviews.
                            </p>
                            <div className="mt-6 flex flex-col gap-2 text-sm">
                                <a href={CONTACT.emailHref} className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-2">
                                    <Mail className="h-4 w-4" /> {CONTACT.email}
                                </a>
                                <a href={CONTACT.phoneHref} className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-2">
                                    <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
